import Button from "@material-ui/core/Button"
import Chip from "@material-ui/core/Chip"
import CircularProgress from "@material-ui/core/CircularProgress"
import List from "@material-ui/core/List"
import ListItem from "@material-ui/core/ListItem"
import ListItemIcon from "@material-ui/core/ListItemIcon"
import AppLanguageSwitch from "../../LanguageSwitch"
import Logout from "../../Logout"
import ListItemText from "@material-ui/core/ListItemText"
import Typography from "@material-ui/core/Typography"
import ClickAwayListener from "@material-ui/core/ClickAwayListener"
import Grow from "@material-ui/core/Grow"
import Paper from "@material-ui/core/Paper"
import Popper from "@material-ui/core/Popper"
import { useRef, useState } from "react"
import { ExternalLink, MessageSquare } from "react-feather"
import { COLOR_BLACK, CONTACT_SUPPORT_URL } from "../../../constants"
import { Link } from "react-router-dom"
import { paths } from "../../../paths"
import { useUrlWithContext } from "../../../hooks/use-url-with-context"
import { useI18n } from "../../../hooks"
import { useGetUserInfoQuery } from "../../../users/api"
import { Menu as MenuIcon, User as UserIcon } from "react-feather"
import { makeStyles, Theme } from "@material-ui/core/styles"

const UserMenu = () => {
  const classes = useStyles()
  const anchorRef = useRef(null)
  const { data: user, isLoading } = useGetUserInfoQuery()
  const { createPathWithContext } = useUrlWithContext()
  const translations = useTranslations()
  const [open, setOpen] = useState(false)
  const handleClose = () => setOpen(false)
  const toggleMenu = () => setOpen(!open)

  if (isLoading || !user) return <CircularProgress />

  const userSettingsPath = createPathWithContext(paths.userSettings())

  return (
    <>
      <Chip
        className={classes.userMenuChip}
        onClick={toggleMenu}
        icon={<UserIcon size={16} />}
        label={<MenuIcon size={16} />}
        ref={anchorRef}
        data-test="user-menu"
      />
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        placement="bottom-end"
        style={{ zIndex: 99 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper elevation={1}>
              <ClickAwayListener onClickAway={handleClose}>
                <span>
                  <List>
                    <ListItem divider dense>
                      <ListItemText
                        primary={user.fullname}
                        secondary={
                          <>
                            <Typography variant="body2" color="textSecondary">
                              {user.email}
                            </Typography>
                            <Button
                              endIcon={<ExternalLink size={16} />}
                              component={Link}
                              to={userSettingsPath}
                              style={{ marginLeft: -4, marginTop: 12 }}
                              size="small"
                              color="primary"
                            >
                              {translations.settings}
                            </Button>
                          </>
                        }
                        primaryTypographyProps={{
                          variant: "body1",
                        }}
                        secondaryTypographyProps={{
                          variant: "subtitle2",
                          color: "textSecondary",
                        }}
                      />
                    </ListItem>
                    <ListItem>
                      <AppLanguageSwitch />
                    </ListItem>
                    <Logout />
                    <ListItem button component="a" href={CONTACT_SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                      <ListItemIcon style={{ minWidth: 40 }}>
                        <MessageSquare size={16} color={COLOR_BLACK} />
                      </ListItemIcon>
                      <ListItemText>{translations.contactSupport}</ListItemText>
                    </ListItem>
                  </List>
                </span>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  userMenuChip: {
    fontSize: 20,
    height: 28,
    paddingLeft: theme.spacing(0.5),
    color: theme.palette.common.black,
    backgroundColor: theme.palette.divider,
    "& .MuiChip-label": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      paddingLeft: 10,
    },
    "& .MuiChip-icon": {
      color: theme.palette.common.black,
    },
  },
}))

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { translations = {} } = useI18n("translation")

  return {
    contactSupport: translations.contactSupport || defaults.contactSupport,
    settings: translations.settings || defaults.settings,
  }
}

const defaultTranslations = {
  contactSupport: "Contact support",
  settings: "Settings",
}

type Translations = typeof defaultTranslations

export default UserMenu
